/** Avisos al celular: lógica pura (qué mostrar según el navegador). Lo que toca el navegador está en `navegadorPush.ts`. */

export type EstadoPush = "ios-sin-instalar" | "sin-soporte" | "no-habilitado" | "bloqueado" | "disponible" | "activado";

export interface EntornoPush {
  userAgent: string;
  /** Abierta desde el ícono de la pantalla de inicio (sin barra del navegador). */
  instalada: boolean;
  /** El navegador tiene service worker + PushManager + Notification. */
  soportaPush: boolean;
  permiso: "default" | "granted" | "denied" | null;
  /** Endpoint de la suscripción de este navegador, si ya está suscripto. */
  endpoint: string | null;
}

export const RUTA_CLAVE_PUBLICA = "/api/push/clave-publica";
export const RUTA_DISPOSITIVOS = "/api/push/dispositivos";
export const rutaDispositivo = (id: number) => `${RUTA_DISPOSITIVOS}/${id}`;

export const esIos = (ua: string) => /iPhone|iPad|iPod/.test(ua);

/**
 * En iPhone, Safari sólo ofrece push con la app instalada: por eso "sin instalar" va antes que "sin soporte".
 * `habilitado` = el backend tiene claves VAPID (la clave pública respondió).
 * `registradoEnServidor` = el endpoint de esta suscripción aparece en `GET /api/push/dispositivos` de la persona
 * logueada. Si el navegador está suscripto pero el servidor no lo tiene (lo borraron desde otro dispositivo, el
 * `POST` falló, o es un celular compartido registrado a otra persona), no llegan avisos: mostramos "disponible"
 * para que "Activar" lo vuelva a registrar (`pushManager.subscribe` devuelve la suscripción existente).
 */
export function estadoPush(e: EntornoPush, habilitado: boolean, registradoEnServidor: boolean): EstadoPush {
  if (esIos(e.userAgent) && !e.instalada) return "ios-sin-instalar";
  if (!e.soportaPush) return "sin-soporte";
  if (!habilitado) return "no-habilitado";
  if (e.permiso === "denied") return "bloqueado";
  if (e.endpoint && registradoEnServidor) return "activado";
  return "disponible";
}

const EQUIPOS: [RegExp, string][] = [
  [/iPhone/, "iPhone"],
  [/iPad/, "iPad"],
  [/Android/, "Android"],
  [/Macintosh/, "Mac"],
  [/Windows/, "Windows"],
  [/Linux/, "Linux"],
];

const NAVEGADORES: [RegExp, string][] = [
  [/Edg\//, "Edge"],
  [/Firefox|FxiOS/, "Firefox"],
  [/CriOS|Chrome\//, "Chrome"],
  [/Safari\//, "Safari"],
];

export function nombreDispositivo(ua: string): string {
  const equipo = EQUIPOS.find(([regex]) => regex.test(ua))?.[1] ?? "Dispositivo";
  const navegador = NAVEGADORES.find(([regex]) => regex.test(ua))?.[1] ?? "Navegador";
  return `${equipo} · ${navegador}`;
}

/** La clave pública VAPID viene en base64url; `pushManager.subscribe` la quiere en bytes. */
export function claveABytes(b64url: string): Uint8Array {
  const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (b64url.length % 4)) % 4);
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}
